import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ProfileIcon, Tag, Button } from '@mercai/clever'

import { CardInfo, SelectStatus, SelectStatusOptionProps } from '@/components'
import { ROUTES_ADMIN } from '@/router'

import type { CardOrderProfileProps } from './card-order-profile.type'

import styles from './card-order-profile.module.css'

import { ProfileProps } from '@/types'
import { QUERIES_ADMIN, useUpdateProfile } from '@/services'
import { useQueryClient } from '@tanstack/react-query'
import { onErrorMessage } from '@/helpers'

export const CardOrderProfile = ({
  isLoading,
  buyer,
  saleOrderUuid,
}: CardOrderProfileProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentCardsOrderCenterShow')

  const queryClient = useQueryClient()

  const { mutateAsync } = useUpdateProfile()

  const onUpdate = (success: boolean) => {
    if (success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrderUuid],
      })
    } else {
      onErrorMessage(
        t('orderStatusesErrorMessageTitle'),
        t('orderStatusesErrorMessageDescription'),
      )
    }
  }

  const onUpdateBuyerProfileStatus = async (
    taxStatus: ProfileProps['taxStatus'],
  ) => {
    if (!buyer) return

    const response = await mutateAsync({ uuid: buyer?.uuid || '', taxStatus })

    onUpdate(response.success)
  }

  const buyerProfileOptions = (
    tEnums: (val: string) => string,
  ): SelectStatusOptionProps[] => [
    {
      label: tEnums('profile_tax_status_completed'),
      value: 'completed',
      variant: 'success',
    },
    {
      label: tEnums('profile_tax_status_review'),
      value: 'review',
      variant: 'danger',
    },
    {
      label: tEnums('profile_tax_status_pending'),
      value: 'pending',
      variant: 'warning',
    },
    {
      label: tEnums('profile_tax_status_invalid'),
      value: 'invalid',
      variant: 'danger',
    },
  ]

  const customerLevelObject: Record<string, ReactNode> = {
    unknown: (
      <Tag variant="basic" model="light" size="small">
        {tEnums('profile_customer_unknown')}
      </Tag>
    ),
    UQL: (
      <Tag variant="warning" model="light" size="small">
        {tEnums('profile_customer_uql')}
      </Tag>
    ),
    MQL: (
      <Tag variant="primary" model="light" size="small">
        {tEnums('profile_customer_mql')}
      </Tag>
    ),
    SQL: (
      <Tag variant="success" model="light" size="small">
        {tEnums('profile_customer_sql')}
      </Tag>
    ),
    customer: (
      <Tag variant="success" model="fill" size="small">
        {tEnums('profile_customer_customer')}
      </Tag>
    ),
    disqualified: (
      <Tag variant="danger" model="light" size="small">
        {tEnums('profile_customer_disqualified')}
      </Tag>
    ),
  }

  return (
    <CardInfo
      isLoading={isLoading}
      icon={<ProfileIcon />}
      title={t('orderProfileTitle')}
      actions={
        <>
          <Button
            size="small"
            variant="success-light"
            href={ROUTES_ADMIN.PROFILE.SHOW.fullPath(buyer?.uuid || '')}
            target="_blank"
            className={styles.link}
          >
            {t('orderProfileLink')}
          </Button>
        </>
      }
      items={[
        {
          label: t('orderProfileStatus'),
          value: (
            <SelectStatus
              size={'small'}
              status={buyer?.taxStatus}
              options={buyerProfileOptions(tEnums)}
              onChange={async (status) =>
                await onUpdateBuyerProfileStatus(
                  status as ProfileProps['taxStatus'],
                )
              }
            />
          ),
        },
        {
          label: t('orderProfileLevel'),
          value: customerLevelObject?.[buyer?.customerLevel || 'unknown'],
        },
        {
          label: t('orderProfileMoscow'),
          value: tEnums(
            `profile_moscow_${buyer?.moscowClassification || 'unknown'}`,
          ),
        },
        {
          label: t('orderProfileName'),
          value: buyer?.usersNames ? buyer?.usersNames?.join(', ') : '-',
        },
        {
          label: t('orderProfileCompanyName'),
          value: buyer?.companyName || '-',
        },
        {
          label: t('orderProfileEmail'),
          value: buyer?.email || '-',
        },
        {
          label: t('orderProfilePhone'),
          value: buyer?.phone || '-',
        },
      ]}
    />
  )
}
